import { Grid, Text, csx } from '@vtex/admin-ui'
import type { HTMLAttributes } from 'react'
import React, { useEffect, useRef } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useLocalizationInput } from '@vtexlab/assisted-sales-localization'

import {
  Step,
  StepTitle,
  StepSubmitButton,
  StepOrderSummary,
  LocalizationInput,
} from '../../../../components'
import { useStep } from '../../../../hooks'
import type { ClientDataAddress, ClientDataState } from '../../../../typings'
import { PostalCodeText } from '../PostalCodeText'
import { formatAddressToExtended } from '../../../../utils'

export type AddressNeighborhoodStepProps = {
  address: ClientDataAddress
  neighborhoodApiValue: string
  onStepSubmit?: (
    addressNeighborhood?: ClientDataState['address']['neighborhood']
  ) => void
} & HTMLAttributes<HTMLFormElement>

export const AddressNeighborhoodStep = ({
  address,
  neighborhoodApiValue,
  onStepSubmit = () => {},
  ...props
}: AddressNeighborhoodStepProps) => {
  const { formatMessage } = useIntl()
  const inputNeighborhoodRef = useRef<HTMLInputElement>(null)

  const neighborhoodInput = useLocalizationInput({
    inputField: 'neighborhood',
    initialValue: address.neighborhood,
    apiValue: neighborhoodApiValue,
  })

  const getNeighborhoodParent = () => {
    if (neighborhoodInput.type === 'select') {
      return address[neighborhoodInput.parent]
    }

    return undefined
  }

  const isNeighborhoodValid =
    (neighborhoodInput.type === 'text' && neighborhoodInput.validate()) ||
    (neighborhoodInput.type === 'select' &&
      neighborhoodInput.value &&
      neighborhoodInput.value !== neighborhoodApiValue)

  const { formRef, handleSubmit, fireFormSubmit } = useStep({
    onSubmit() {
      onStepSubmit(neighborhoodInput.value)
    },
  })

  useEffect(() => {
    inputNeighborhoodRef.current?.focus()
  }, [])

  return (
    <Step
      ref={formRef}
      noValidate
      onSubmit={handleSubmit}
      className={csx({
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        '@desktop': {
          justifyContent: 'space-between',
        },
      })}
      {...props}
    >
      <StepTitle>
        <FormattedMessage id="checkout.home-delivery.step.confirm-address.neighborhood-title" />
      </StepTitle>
      <div
        className={csx({
          display: 'flex',
          justifyContent: 'center',
          height: 'full',
          alignItems: 'center',
          flex: 1,
        })}
      >
        <div className={csx({ flex: 1 })}>
          <Grid className={csx({ marginBottom: '$space-3', rowGap: '0.5rem' })}>
            <PostalCodeText value={address.postalCode} />
            <Text variant="pageTitle" tone="secondary">
              {formatAddressToExtended({
                ...address,
                neighborhood: neighborhoodInput.value,
              })}
            </Text>
          </Grid>
          <LocalizationInput
            localizationInput={neighborhoodInput}
            parent={getNeighborhoodParent()}
            aria-label={formatMessage({
              id: 'checkout.home-delivery.step.confirm-address.neighborhood',
            })}
          />
        </div>
      </div>
      <StepOrderSummary>
        <StepSubmitButton
          type="submit"
          onClick={fireFormSubmit}
          disabled={!isNeighborhoodValid}
        >
          <FormattedMessage id="continue" />
        </StepSubmitButton>
      </StepOrderSummary>
    </Step>
  )
}
